import { useState } from "react";
import { ResultsApi, Configuration } from "manager-common";
import { useAuth } from "react-oidc-context";
import { useApiConfig, SERVER_ERROR_MESSAGE } from "common-fe";
import { useTranslation } from "i18n-fe";
import {
  getMessageTranslationKey,
  getDescriptionTranslationKey,
} from "../utils/usefResults";

const MANAGER_API_BASE_URL = import.meta.env.VITE_MANAGER_API_BASE_URL;

/**
 * Hook for uploading results files
 * Handles file upload to the results API
 */
export function useUploadResults() {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadResponse, setUploadResponse] = useState(null);
  const auth = useAuth();
  const buildConfig = useApiConfig();

  const { t } = useTranslation("common");

  const upload = async (file) => {
    if (!file) {
      setError(t("results.upload.errors.noFile"));
      return;
    }

    if (!auth.user?.access_token) {
      setError(t("shows.detail.authError"));
      return;
    }

    setUploading(true);
    setError(null);
    setUploadResponse(null);

    try {
      const api = new ResultsApi(
        new Configuration(buildConfig(MANAGER_API_BASE_URL))
      );

      // For file uploads, we need to use FormData
      const formData = new FormData();
      formData.append("file", file);

      // Use the uploadResults method with FormData in options
      // Don't set Content-Type header - browser will set it with boundary
      const { data } = await api.uploadResults({
        data: formData,
      });

      setUploadResponse(data);
      setError(null);
    } catch (err) {
      console.error("Upload error:", err);

      // Check if this is an ownership error (403 with specific error message)
      if (
        err.response?.status === 403 &&
        err.response?.data?.error === "Competition owned by another user"
      ) {
        setError({
          type: "ownershipError",
          message: err.response.data.message || err.response.data.error,
          messageKey: "results.upload.errors.ownershipError",
          descriptionKey: "results.upload.descriptions.ownershipError",
        });
      } else if (err.response?.data?.message) {
        // Check if this is a competition status error with message/description
        const messageKey = getMessageTranslationKey(err.response.data.message);
        const descriptionKey = getDescriptionTranslationKey(
          err.response.data.message
        );

        // Store error object with translation keys for the component to use
        setError({
          type: "competitionStatus",
          message: err.response.data.message,
          messageKey,
          descriptionKey,
          competition: err.response.data.competition,
        });
      } else {
        // Regular error
        const errorMessage = SERVER_ERROR_MESSAGE;
        setError(errorMessage);
      }
      // Don't throw - let the component handle the error state
    } finally {
      setUploading(false);
    }
  };

  const publish = async (actions) => {
    if (!actions || !Array.isArray(actions) || actions.length === 0) {
      setError(t("results.upload.errors.noIds"));
      return;
    }

    if (!auth.user?.access_token) {
      setError(t("shows.detail.authError"));
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const api = new ResultsApi(
        new Configuration(buildConfig(MANAGER_API_BASE_URL))
      );

      const response = await api.publishResultGroup({ actions });
      const data = response.data;

      setUploadResponse((prev) => ({
        ...prev,
        publishResponse: data,
      }));
      setError(null);
      return data;
    } catch (err) {
      console.error("Publish error:", err);
      const errorMessage =
        err.message || t("results.upload.errors.publishFailed");
      setError(errorMessage);
      throw err;
    } finally {
      setUploading(false);
    }
  };

  const reset = () => {
    setError(null);
    // Don't reset uploading or uploadResponse here - let component control those
  };

  return {
    uploading,
    error,
    uploadResponse,
    upload,
    publish,
    reset,
  };
}
